import type { ReactNode, RefObject } from 'react';

import { useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import { AvatarIcon } from '../../images/shapes';

const useOutsideListener = (
  ref: RefObject<HTMLElement | null>,
  handler?: () => void,
  hasEventOnCloseOutside?: boolean,
) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler?.();
      }
    };

    if (hasEventOnCloseOutside)
      document.addEventListener('mousedown', handleClickOutside);

    return () => {
      if (hasEventOnCloseOutside)
        document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, handler, hasEventOnCloseOutside]);
};

export interface SectionUserMenuItemProps {
  id?: string;
  text: string;
  icon?: ReactNode;
  link?: string;
  onClick?: () => void;
  selected?: boolean;
}

interface SectionUserMenuProps {
  /**
   * Items
   */
  items?: SectionUserMenuItemProps[];
  /**
   * User avatar
   */
  avatar?: any;
  /**
   * User name
   */
  name?: string;
  /**
   * User email
   */
  email?: string;
  /**
   * handle Close is used to triggered option on close
   */
  handleClose?: () => void;
  /**
   * If we want to show modal On Close Outside mode
   */
  isModalOnCloseOutside?: boolean;
  /**
   * component min Width only on mode when is outside `isModalOnCloseOutside`: *true*
   */
  minWidth?: number;
  /**
   * Modal position on left used on closed sidebar
   */
  leftOnOutside?: number;
  /**
   * Used for defined if we want to move left on closed sidebar
   */
  isOpen?: boolean;
  /**
   * additional User Menu Container Classes
   */
  additionalUserMenuContainerClasses?: string;
  /**
   * additional User Menu Classes
   */
  additionalUserMenuClasses?: string;
  /**
   * Add event to close modal after click outside
   */
  hasEventOnCloseOutside?: boolean;
  /**
   * Component test id
   */
  testId?: string;
}

export const SectionUserMenu = ({
  items,
  avatar,
  name,
  email,
  handleClose,
  isModalOnCloseOutside = false,
  leftOnOutside = 0,
  isOpen = false,
  minWidth = 250,
  additionalUserMenuContainerClasses,
  additionalUserMenuClasses,
  hasEventOnCloseOutside = false,
  testId,
}: SectionUserMenuProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useOutsideListener(wrapperRef, handleClose, hasEventOnCloseOutside);

  const handleClick = (onclick?: () => void) => {
    // Case: On click close modal
    handleClose?.();

    onclick?.();
  };

  return (
    <div
      ref={wrapperRef}
      className={twMerge(
        'absolute bottom-2 rounded-sm bg-white dark:bg-slate-900 py-3 z-10 shadow-modal overflow-hidden',
        additionalUserMenuContainerClasses,
      )}
      style={{
        minWidth: isModalOnCloseOutside ? minWidth : 'calc(100% - 32px)',
        left: isModalOnCloseOutside && !isOpen ? leftOnOutside + 10 : 24,
      }}
      {...getTestProps(testId)}
    >
      <div className="flex items-center justify-center pb-3 mb-3 border-b dark:border-slate-800">
        {avatar && (
          <AvatarIcon
            className={twMerge('w-12 pl-3 flex select-none text-blue')}
          />
        )}

        <div
          className={twMerge(
            'relative flex flex-col ml-3 overflow-hidden dark:text-slate-200 w-full',
            'transition-all ease-linear duration-fast',
          )}
        >
          <div
            title={name && name?.length > 13 ? name : undefined}
            className={twMerge(
              'text-base font-medium whitespace-nowrap relative overflow-hidden text-ellipsis pr-2',
            )}
            {...getTestProps(testId, 'name')}
          >
            {name}
          </div>

          <div
            title={email && email?.length > 19 ? email : undefined}
            className={twMerge('text-[13px] whitespace-nowrap pr-2')}
            {...getTestProps(testId, 'email')}
          >
            {email}
          </div>
        </div>
      </div>
      <ul>
        {items?.map((el) => {
          const elID = el.id || el.text;
          return (
            <li
              key={elID}
              className={twMerge(
                'flex flex-nowrap text-sm hover:bg-gradient-menu-hover',
                el.selected && 'bg-gradient-menu-hover',
                additionalUserMenuClasses,
              )}
              {...getTestProps(testId, elID)}
            >
              {el.link ? (
                <Link
                  className="flex items-center leading-none dark:text-slate-200 w-full py-2 pl-4"
                  to={el.link}
                  onClick={() => handleClick(el.onClick)}
                  {...getTestProps(testId, `link-${elID}`)}
                >
                  {el.icon}
                  <div className="ml-2 whitespace-nowrap pr-2">{el.text}</div>
                </Link>
              ) : (
                <button
                  type="button"
                  className="flex items-center leading-none py-2 pl-4 w-full cursor-pointer"
                  onClick={() => handleClick(el.onClick)}
                  {...getTestProps(testId, `text-${elID}`)}
                >
                  {el.icon}
                  <div className="ml-2 whitespace-nowrap dark:text-slate-200 pr-2">
                    {el.text}
                  </div>
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
